import { Injectable, inject } from '@angular/core';
import { BackendError } from '../../domain/error.domain';
import { LocaleService } from '../../services/locale/locale.service';
import { LinkRegistryService } from '../../services/link-registry.service';
import { FrequencyTypes } from './cash/cash-transfer.domain';
import { ServerStatus } from './securities/securities-transfers.domain';

@Injectable()
export class TransferService {
  private readonly linkRegistryService = inject(LinkRegistryService);
  private readonly localeService = inject(LocaleService);

  static readonly KNOWN_WMM_ERROR_CODE: string = 'JOURNAL.CONFIRM.IBM.OFFLINE.REJECT';
  static readonly KNOWN_WMM_CASH_TRANSFER_STATUS_CODES: Array<string> = [
    'JOURNAL.CONFIRM.CIK.SUCCESS',
    'JOURNAL.CONFIRM.IBM.OFFLINE.PROCESSING'
  ];

  static readonly KNOWN_WMM_SECURITY_TRANSFER_STATUS_CODES: Array<string> = [
    'JOURNAL.MANUAL.RDSP',
    'JOURNAL.MANUAL.CREDIT.RDSP',
    'JOURNAL.CREDIT.NON.STP',
    'JOURNAL.CONFIRM.CIK.SUCCESS',
    'JOURNAL.CONFIRM.IBM.OFFLINE.PROCESSING',
    'JOURNAL.CONFIRM.MANUAL.PROCESSING'
  ];

  createCallUsTransferError(code: string): BackendError {
    return {
      type: 'APPLICATION',
      code,
      arguments: [{ PHONE_NUMBER: this.getCallUsPhoneNumber() }]
    };
  }

  getCallUsPhoneNumber(): string {
    return this.linkRegistryService.getLink('display_contact_direct_investing');
  }

  displayEnglishQuebecDisclaimer(): boolean {
    return this.localeService.getLanguage() === 'en';
  }

  getFrequencyValueForTagging(frequency: FrequencyTypes): string {
    // Adding this method here so that can be used for both OFI and Cash transfers for WB and ET for tagging.
    switch (frequency) {
      case FrequencyTypes.WEEKLY:
        return 'weekly';
      case FrequencyTypes.BI_WEEKLY:
        return 'bi-weekly';
      case FrequencyTypes.SEMI_MONTHLY:
        return 'semi-monthly';
      case FrequencyTypes.MONTHLY:
        return 'monthly';
      default:
        return 'one-time';
    }
  }

  setWmmTransferStatusCodes(statuses: Array<ServerStatus>, isCashTransfer: boolean = false): Array<string> {
    const knownStatusCodeList = isCashTransfer ?
      TransferService.KNOWN_WMM_CASH_TRANSFER_STATUS_CODES : TransferService.KNOWN_WMM_SECURITY_TRANSFER_STATUS_CODES;
    const statusCodes: Array<string> = [];
    if (statuses?.length > 0) {
      statuses.forEach((status: ServerStatus): void => {
        if (knownStatusCodeList.includes(status.serverStatusCode)) {
          statusCodes.push(status.serverStatusCode);
        }
      });
    }
    // if we did not receive any codes, or if none of the codes are recognized, then display a message saying that
    // we do not know the status of their transfer
    if (statusCodes.length === 0) {
      statusCodes.push('UNKNOWN');
    }
    return statusCodes;
  }

  getWmmErrorCode(errorResponse: Array<BackendError>): Array<string> {
    let hasKnownError = false;
    errorResponse.forEach((error:BackendError): void => {
      if (error.code === TransferService.KNOWN_WMM_ERROR_CODE) {
        hasKnownError = true;
      }
    });
    return hasKnownError ? [TransferService.KNOWN_WMM_ERROR_CODE] : ['UNKNOWN'];
  }
}
